(function () {
  if (window.CartGiftManager) return;

  class CartGiftManager {
    constructor() {
      this.resolvers = [];
      this.currentPromise = null;
      this.pending = false;
      this.initialized = false;
      this.hasRunCartReady = false;

      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => this.initialize(), { once: true });
      } else {
        this.initialize();
      }
    }

    initialize() {
      if (this.initialized) return;
      this.cartApi = window.CartApi;
      if (!this.cartApi) {
        console.error("Cart gift manager requires window.CartApi");
        return;
      }
      this.initialized = true;
      const previous = this.cartApi.afterMutation;
      this.cartApi.afterMutation = async (context) => {
        if (previous) {
          context = { ...context, result: (await previous.call(this.cartApi, context)) || context.result };
        }
        return this.afterMutation(context);
      };
      this.runCartReady();
    }

    register(id, resolver) {
      if (!id || typeof resolver !== "function") return;
      this.resolvers = this.resolvers.filter((entry) => entry.id !== id);
      this.resolvers.push({ id, resolver });
      this.runCartReady();
    }

    async runCartReady() {
      if (!this.initialized || this.hasRunCartReady || !this.resolvers.length) return;
      this.hasRunCartReady = true;
      try {
        const { cart, changed } = await this.run();
        if (!changed) return;
        // A concurrent mutation owns rendering while the drawer is busy.
        await window.CartDrawer?.refresh();
        if (cart) document.dispatchEvent(new CustomEvent("cart:updated", { detail: cart }));
      } catch (error) {
        console.error("Cart gift ready update failed:", error);
      }
    }

    async afterMutation({ action, result, options }) {
      if (!this.resolvers.length) return result;
      const { cart, changed } = await this.run();
      let reconciled = action === "add" || !cart ? { ...result } : { ...result, ...cart };
      if (changed && options.sections?.length) {
        // Never return pre-gift HTML, even when Section Rendering fails.
        reconciled.sections = null;
        try {
          const url = new URL(options.sectionsUrl || window.location.href, window.location.origin);
          url.searchParams.set("sections", options.sections.join(","));
          const response = await fetch(url.toString());
          if (!response.ok) throw new Error(`Cart sections: ${response.status}`);
          reconciled.sections = await response.json();
        } catch (error) {
          console.error("Cart gift sections refresh failed:", error);
        }
      }
      return reconciled;
    }

    run() {
      if (this.currentPromise) {
        this.pending = true;
        return this.currentPromise;
      }
      // Defer the loop so the shared promise is installed before any resolver runs.
      this.currentPromise = Promise.resolve().then(() => this.runLoop()).finally(() => {
        this.currentPromise = null;
      });
      return this.currentPromise;
    }

    async runLoop() {
      let cart = null;
      let changed = false;
      do {
        this.pending = false;
        try {
          cart = await this.cartApi.get();
          if (!Array.isArray(cart?.items)) throw new Error("Invalid cart response");
          const changes = await this.resolveChanges(cart);
          if (changes.updates.length) {
            const updates = Object.fromEntries(changes.updates.map((item) => [item.key || item.id, Number(item.quantity || 0)]));
            cart = await this.cartApi.update(updates, { skipAfterMutation: true });
            changed = true;
          }
          if (changes.adds.length) {
            await this.cartApi.add(changes.adds, { skipAfterMutation: true });
            changed = true;
            cart = await this.cartApi.get();
          }
        } catch (error) {
          console.error("Cart gift update failed:", error);
          // A failed response may still have committed a write on the server.
          // Invalidate earlier sections and recover the latest cart when possible.
          changed = true;
          try {
            cart = await this.cartApi.get();
          } catch (readError) {
            cart = null;
            console.error("Cart gift recovery failed:", readError);
          }
        }
      } while (this.pending);
      return { cart, changed };
    }

    async resolveChanges(cart) {
      const updates = [];
      const adds = [];

      for (const entry of this.resolvers) {
        try {
          const result = await entry.resolver(cart);
          if (!result) continue;

          if (Array.isArray(result.updates)) {
            result.updates.forEach((update) => {
              if (!update) return;
              updates.push(update);
            });
          }

          if (Array.isArray(result.adds)) {
            result.adds.forEach((add) => {
              if (!add) return;
              adds.push(add);
            });
          }
        } catch (error) {
          console.error(`Cart gift resolver "${entry.id}" failed:`, error);
        }
      }

      return {
        updates: this.dedupeUpdates(updates),
        adds: this.dedupeAdds(adds),
      };
    }

    dedupeUpdates(updates) {
      const updatesByTarget = {};

      updates.forEach((update) => {
        const target = update.key || update.id;
        if (!target && target !== 0) return;
        updatesByTarget[String(target)] = update;
      });

      return Object.values(updatesByTarget);
    }

    dedupeAdds(adds) {
      const seen = {};
      const result = [];

      adds.forEach((add) => {
        const variantId = add.id || add.variantId;
        if (!variantId) return;

        const properties = add.properties || {};
        const source = properties.__dreame_gift_source || add.source || "";
        const key = `${source}|${variantId}|${JSON.stringify(properties)}`;

        if (seen[key]) return;
        seen[key] = true;
        const quantity = Number(add.quantity || 1);

        result.push({
          id: Number(variantId),
          quantity: Number.isFinite(quantity) ? Math.max(1, quantity) : 1,
          properties,
        });
      });

      return result;
    }
  }

  window.CartGiftManager = new CartGiftManager();
})();
